import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DonationSetup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '50',
    frequency: 'one-time',
    processor: 'stripe',
    name: '',
    email: '',
    address: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Initialize scripts if needed
    if (window.initPathioScripts) {
      window.initPathioScripts();
    }
  }, []);

  const presetAmounts = [5, 10, 25, 50, 100, 250, 500];
  const donationFrequencies = [
    { value: 'one-time', label: 'One-Time Donation' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' }
  ];

  const paymentProcessors = [
    { id: 'stripe', name: 'Stripe', fee: '2.2% + $0.30', icon: '💳' },
    { id: 'paypal', name: 'PayPal', fee: '2.2% + $0.30', icon: '🅿️' },
    { id: 'square', name: 'Square', fee: '2.6% + $0.30', icon: '⬛' },
    { id: 'donorbox', name: 'Donorbox', fee: '1.5% + $0.30', icon: '🎁' },
    { id: 'giveorg', name: 'Give.org', fee: '2%', icon: '❤️' },
    { id: 'bank', name: 'Bank Transfer', fee: 'No fee', icon: '🏦' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePresetAmount = (amount) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString()
    }));
  };

  const handleNext = () => {
    if (validateForm()) {
      // Store donation data in sessionStorage for next step
      sessionStorage.setItem('donationData', JSON.stringify(formData));
      navigate('/donations/intent');
    }
  };

  const handleBack = () => {
    navigate('/donations');
  };

  return (
    <>
      <Preloader />
      <Sidebar />
      <Header />

      <main>
        <section className="ul-breadcrumb ul-section-spacing">
          <div className="ul-container">
            <h2 className="ul-breadcrumb-title">Support Our Organization</h2>
            <ul className="ul-breadcrumb-nav">
              <li><a href="/">Home</a></li>
              <li><span className="separator"><i className="flaticon-right"></i></span></li>
              <li>Donations</li>
            </ul>
          </div>
        </section>

        <section className="ul-donation-setup ul-section-spacing">
          <div className="ul-container">
            {/* Progress Indicator */}
            <div className="ul-donation-progress">
              <div className="ul-progress-steps">
                <div className="ul-progress-step active">
                  <span className="step-number">1</span>
                  <span className="step-label">Setup</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">2</span>
                  <span className="step-label">Intent</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">3</span>
                  <span className="step-label">Preferences</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">4</span>
                  <span className="step-label">Payment</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">5</span>
                  <span className="step-label">Confirm</span>
                </div>
              </div>
            </div>

            <div className="ul-donation-form-wrapper">
              <div className="ul-donation-form">
                <h2>Step 1: Choose Your Donation Amount & Method</h2>

                {/* Donation Amount */}
                <div className="ul-form-group">
                  <label className="ul-form-label">Donation Amount *</label>
                  <div className="ul-preset-amounts">
                    {presetAmounts.map(amount => (
                      <button
                        key={amount}
                        type="button"
                        className={`ul-preset-btn ${formData.amount === amount.toString() ? 'active' : ''}`}
                        onClick={() => handlePresetAmount(amount)}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  <div className="ul-custom-amount">
                    <label>Or enter custom amount:</label>
                    <div className="ul-input-group">
                      <span className="currency-symbol">$</span>
                      <input
                        type="number"
                        name="amount"
                        min="1"
                        step="0.01"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="Enter amount"
                        className="ul-form-input"
                      />
                    </div>
                  </div>
                  {errors.amount && <span className="error-message">{errors.amount}</span>}
                </div>

                {/* Donation Frequency */}
                <div className="ul-form-group">
                  <label className="ul-form-label">Donation Type *</label>
                  <div className="ul-frequency-options">
                    {donationFrequencies.map(freq => (
                      <label key={freq.value} className="ul-radio-option">
                        <input
                          type="radio"
                          name="frequency"
                          value={freq.value}
                          checked={formData.frequency === freq.value}
                          onChange={handleInputChange}
                        />
                        <span className="radio-label">{freq.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Payment Processor Selection */}
                <div className="ul-form-group">
                  <label className="ul-form-label">Payment Processor *</label>
                  <div className="ul-processor-options">
                    {paymentProcessors.map(processor => (
                      <label key={processor.id} className="ul-processor-option">
                        <input
                          type="radio"
                          name="processor"
                          value={processor.id}
                          checked={formData.processor === processor.id}
                          onChange={handleInputChange}
                        />
                        <span className="processor-card">
                          <span className="processor-icon">{processor.icon}</span>
                          <span className="processor-info">
                            <span className="processor-name">{processor.name}</span>
                            <span className="processor-fee">Fee: {processor.fee}</span>
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Donor Information */}
                <div className="ul-donor-info">
                  <h3>Your Information</h3>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className={`ul-form-input ${errors.name ? 'error' : ''}`}
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      className={`ul-form-input ${errors.email ? 'error' : ''}`}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Address (Optional)</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter your address"
                      className="ul-form-input"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="ul-form-actions">
                  <button 
                    type="button"
                    className="ul-btn-secondary"
                    onClick={handleBack}
                  >
                    Back
                  </button>
                  <button 
                    type="button"
                    className="ul-btn ul-btn-primary"
                    onClick={handleNext}
                  >
                    Next Step →
                  </button>
                </div>
              </div>

              {/* Info Sidebar */}
              <div className="ul-donation-info-sidebar">
                <div className="ul-info-card">
                  <h3>💰 Your Donation</h3>
                  <div className="ul-info-item">
                    <span>Amount:</span>
                    <strong>${formData.amount}</strong>
                  </div>
                  <div className="ul-info-item">
                    <span>Type:</span>
                    <strong>
                      {donationFrequencies.find(f => f.value === formData.frequency)?.label}
                    </strong>
                  </div>
                  <div className="ul-info-item">
                    <span>Processor:</span>
                    <strong>
                      {paymentProcessors.find(p => p.id === formData.processor)?.name}
                    </strong>
                  </div>
                </div>

                <div className="ul-info-card">
                  <h3>✅ Why Donate?</h3>
                  <ul className="ul-benefits-list">
                    <li>Support health education in underserved communities</li>
                    <li>Tax-deductible donation (501(c)(3) nonprofit)</li>
                    <li>Receive donation receipt and impact report</li>
                    <li>Join our community of supporters</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DonationSetup;
